import React, { useState } from 'react';
import { proService } from '../../services';
import { localDateValue, localTimeValue, moveToDate, moveToTime } from '../../utils/proSessionPricing';
import { bookingDay, bookingClock } from '../../utils/proBookingTime';

/**
 * RESCHEDULE and CANCEL on an offer or a paid booking (Nov 2026; testers had only cancel-and-rebook
 * before paying and only Report a problem after). The server decides what each side may do and what
 * a cancel would refund — `offer.canReschedule`, `offer.cancelQuote`, `offer.pendingReschedule` —
 * and this only draws it. Cancelling is two taps: the first shows what happens to the money.
 */
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const when = (iso) => `${bookingDay(iso)} · ${bookingClock(iso)}`;

/** Sessions that haven't started, in order: the ones a new time can move. */
export function sessionsToCome(offer, now = Date.now()) {
  const list = offer.sessionCount > 1 && offer.sessions?.length
    ? offer.sessions
    : offer.startsAt ? [{ number: 1, startsAt: offer.startsAt, endsAt: offer.endsAt }] : [];
  return list.filter((s) => new Date(s.startsAt).getTime() > now);
}

const btn = 'border-2 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest disabled:opacity-50';

export default function BookingChanges({ offer, onChange, pink = '#FF00AA' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [starts, setStarts] = useState([]);
  const [note, setNote] = useState('');
  const [armed, setArmed] = useState(false);
  const [reason, setReason] = useState('');

  const pending = offer.pendingReschedule;
  const quote = offer.cancelQuote;
  if (!pending && !offer.canReschedule && !quote) return null;

  const run = async (fn) => {
    setBusy(true); setError('');
    const r = await fn();
    setBusy(false);
    if (r.success && r.data) { onChange(r.data); setEditing(false); setArmed(false); setNote(''); setReason(''); }
    else setError(r.error || 'That didn’t go through. Try again.');
  };

  const openEditor = () => {
    setStarts(sessionsToCome(offer).map((s) => s.startsAt));
    setEditing(true);
    setArmed(false);
  };
  const sendTimes = () => {
    if (starts.some((s) => new Date(s).getTime() <= Date.now())) { setError('New times have to be in the future.'); return; }
    run(() => proService.proposeReschedule(offer.id, { starts, note: note.trim() || null }));
  };

  return (
    <div className="mt-4 border-2 border-black bg-white p-4" data-testid="booking-changes">
      {pending && (
        <div data-testid="pending-reschedule">
          <div className="font-mono text-[11px] font-bold uppercase tracking-widest" style={{ color: pink }}>
            {pending.awaitingMe ? 'They asked for a new time' : 'You asked for a new time'}
          </div>
          <ul className="mt-2 space-y-1">
            {pending.sessions.filter((s) => new Date(s.startsAt).getTime() > Date.now()).map((s) => (
              <li key={s.number} className="font-mono text-xs">
                {pending.sessions.length > 1 && <span className="font-bold">{s.number}. </span>}{when(s.startsAt)}
              </li>
            ))}
          </ul>
          {pending.note && <p className="mt-1 text-xs text-gray-600">“{pending.note}”</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {pending.awaitingMe ? (
              <>
                <button type="button" disabled={busy} className={`${btn} border-black text-black`} style={{ background: pink }}
                  onClick={() => run(() => proService.answerReschedule(offer.id, pending.id, 'accept'))}>
                  Accept new time
                </button>
                <button type="button" disabled={busy} className={`${btn} border-black bg-white`}
                  onClick={() => run(() => proService.answerReschedule(offer.id, pending.id, 'decline'))}>
                  Keep the old time
                </button>
              </>
            ) : (
              <>
                <span className="self-center font-mono text-[11px] text-gray-500">Waiting for them to answer.</span>
                <button type="button" disabled={busy} className={`${btn} border-gray-300 text-gray-500`}
                  onClick={() => run(() => proService.answerReschedule(offer.id, pending.id, 'withdraw'))}>
                  Take it back
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className={pending ? 'mt-4 border-t-2 border-dashed border-gray-300 pt-3' : ''} data-testid="reschedule-editor">
          <div className="font-mono text-[11px] font-bold uppercase tracking-widest">New time</div>
          <p className="mt-1 font-mono text-[11px] text-gray-500">
            {starts.length > 1 ? 'Each session keeps its length. ' : ''}They’ll be asked to accept it; nothing changes until they do.
          </p>
          <div className="mt-2 space-y-2">
            {starts.map((iso, i) => (
              <div key={i} className="flex items-center gap-2">
                {starts.length > 1 && <span className="w-5 font-mono text-xs font-bold">{i + 1}.</span>}
                <input type="date" aria-label={`Session ${i + 1} date`} value={localDateValue(iso)}
                  onChange={(e) => setStarts((all) => all.map((x, j) => (j === i ? moveToDate(x, e.target.value) : x)))}
                  className="border-2 border-black px-2 py-1.5 font-mono text-xs" />
                <input type="time" aria-label={`Session ${i + 1} start time`} value={localTimeValue(iso)}
                  onChange={(e) => setStarts((all) => all.map((x, j) => (j === i ? moveToTime(x, e.target.value) : x)))}
                  className="border-2 border-black px-2 py-1.5 font-mono text-xs" />
              </div>
            ))}
          </div>
          <textarea value={note} maxLength={500} rows={2} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional)"
            className="mt-2 w-full resize-none border-2 border-black px-3 py-2 font-mono text-xs outline-none" />
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={sendTimes} className={`${btn} border-black text-black`} style={{ background: pink }}>
              {busy ? '…' : 'Send new time'}
            </button>
            <button type="button" disabled={busy} onClick={() => setEditing(false)} className={`${btn} border-black bg-white`}>Never mind</button>
          </div>
        </div>
      )}

      {armed && quote && (
        <div className={pending || editing ? 'mt-4 border-t-2 border-dashed border-gray-300 pt-3' : ''} data-testid="cancel-confirm">
          <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-red-700">Cancel this {quote.kind === 'unpaid' ? 'offer' : 'booking'}?</div>
          <p className="mt-1 font-mono text-xs text-gray-700">{quote.summary}</p>
          {quote.kind !== 'unpaid' && (
            <dl className="mt-2 space-y-0.5 font-mono text-[11px] text-gray-500">
              <div className="flex justify-between"><dt>Back to the card</dt><dd className="font-bold text-black">{money(quote.refundCents)}</dd></div>
              {quote.keptByProCents > 0 && <div className="flex justify-between"><dt>The pro keeps</dt><dd>{money(quote.keptByProCents)}</dd></div>}
            </dl>
          )}
          <textarea value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} placeholder="Tell them why (optional)"
            className="mt-2 w-full resize-none border-2 border-black px-3 py-2 font-mono text-xs outline-none" />
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} className={`${btn} border-red-700 bg-red-700 text-white`}
              onClick={() => run(() => proService.cancelOffer(offer.id, { reason: reason.trim() || null }))}>
              {busy ? '…' : 'Yes, cancel'}
            </button>
            <button type="button" disabled={busy} onClick={() => setArmed(false)} className={`${btn} border-black bg-white`}>Keep it</button>
          </div>
        </div>
      )}

      {!editing && !armed && (
        <div className={`flex flex-wrap gap-2 ${pending ? 'mt-4 border-t-2 border-dashed border-gray-300 pt-3' : ''}`}>
          {offer.canReschedule && (
            <button type="button" disabled={busy} onClick={openEditor} className={`${btn} border-black bg-white hover:bg-gray-100`}>
              {pending && !pending.awaitingMe ? 'Change the new time' : 'Reschedule'}
            </button>
          )}
          {quote && (
            <button type="button" disabled={busy} onClick={() => { setArmed(true); setEditing(false); }}
              className={`${btn} border-gray-300 text-gray-500 hover:border-black hover:text-black`}>
              {quote.kind === 'unpaid' ? 'Cancel offer' : 'Cancel booking'}
            </button>
          )}
        </div>
      )}

      {error && <p className="mt-2 font-mono text-xs text-red-700" role="alert">{error}</p>}
    </div>
  );
}
